import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Mail, Plus } from 'lucide-react';
import { readFragment } from 'gql.tada';
import type { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import {
  createSelectColumn,
  DataTable,
  SortableHeader,
} from '~/components/ui/data-table';
import type { AppLayoutContext } from '~/types';
import { useStaffListQuery } from './useStaffListQuery';
import { StaffMemberFragment } from './StaffMemberFragment';
import CreateStaffMemberDialog from './CreateStaffMemberDialog';
import { useInviteAllStaff } from '~/hooks/useBulkAccountInvites';

type StaffKind = 'TEACHER' | 'AIDE' | 'ADMIN' | 'OTHER';

const KIND_LABEL: Record<StaffKind, string> = {
  TEACHER: 'Teacher',
  AIDE: 'Aide',
  ADMIN: 'Admin',
  OTHER: 'Other',
};

export default function Staff() {
  const { school } = useOutletContext<AppLayoutContext>();
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);
  const [inviteConfirmOpen, setInviteConfirmOpen] = useState(false);
  // What the last "Invite Staff Without Accounts" did, shown above the list.
  const [inviteOutcome, setInviteOutcome] = useState<{ sent: string; skipped: string[] } | null>(null);
  const [inviteErrors, setInviteErrors] = useState<string[]>([]);
  const [inviteAllStaff, { loading: invitingStaff }] = useInviteAllStaff();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'state', value: ['ACTIVE'] },
  ]);

  const activeQuery = useStaffListQuery(school?.id, { state: 'ACTIVE' });
  const inactiveQuery = useStaffListQuery(school?.id, { state: 'INACTIVE' });

  const allStaff = useMemo(
    () => {
      const members = [
        ...(activeQuery.data?.school?.staffMembers ?? []),
        ...(inactiveQuery.data?.school?.staffMembers ?? []),
      ].map((sm) => readFragment(StaffMemberFragment, sm));

      return Array.from(new Map(members.map((member) => [member.id, member])).values());
    },
    [activeQuery.data, inactiveQuery.data]
  );

  const loading = activeQuery.loading || inactiveQuery.loading;

  const refetch = useCallback(async () => {
    await Promise.all([activeQuery.refetch(), inactiveQuery.refetch()]);
  }, [activeQuery, inactiveQuery]);

  const handleRowClick = useCallback(
    (staffMemberId: string) => {
      navigate(`/app/${school?.slug}/staff/${staffMemberId}`);
    },
    [navigate, school?.slug]
  );

  const handleInviteAllStaff = useCallback(async () => {
    if (!school?.id) return;
    setInviteOutcome(null);
    setInviteErrors([]);

    try {
      const result = await inviteAllStaff({ variables: { input: { schoolId: school.id } } });
      const payload = result.data?.inviteAllStaff;
      if (payload?.userErrors?.length) {
        setInviteErrors(payload.userErrors.map((error) => error.message));
        return;
      }

      const sentCount = payload?.sentCount ?? 0;
      setInviteOutcome({
        sent: `Sent ${sentCount} ${sentCount === 1 ? 'invitation' : 'invitations'}; skipped ${payload?.skippedCount ?? 0}.`,
        skipped: payload?.results
          .filter((item) => item.status === 'SKIPPED')
          .map((item) => `${item.person.fullName}: ${item.reason}`) ?? [],
      });
      await refetch();
    } catch (error) {
      setInviteErrors([error instanceof Error ? error.message : 'The invitations were not sent. Please try again.']);
    }
  }, [inviteAllStaff, refetch, school?.id]);

  const columns = useMemo<ColumnDef<any>[]>(() => [
    createSelectColumn<any>(),
    {
      id: 'search',
      accessorFn: (staffMember: any) => [
        staffMember.person.firstName,
        staffMember.person.lastName,
        staffMember.title,
        staffMember.employeeId,
      ].filter(Boolean).join(' '),
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      // Sort the way the cell reads: last name first.
      meta: {
        sortKey: (staffMember: any) =>
          `${staffMember.person.lastName} ${staffMember.person.firstName}`,
      },
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.person.lastName}, {row.original.person.firstName}
        </span>
      ),
    },
    {
      accessorKey: 'kind',
      header: 'Kind',
      cell: ({ row }) => (
        <Badge variant="outline">
          {KIND_LABEL[row.original.kind as StaffKind] ?? row.original.kind}
        </Badge>
      ),
      meta: {
        facet: {
          title: 'Kind',
          getKey: (staffMember: any) => staffMember.kind,
          getLabel: (staffMember: any) =>
            KIND_LABEL[staffMember.kind as StaffKind] ?? staffMember.kind,
        },
      },
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => row.original.title || '—',
    },
    {
      accessorKey: 'employeeId',
      header: 'Employee ID',
      cell: ({ row }) => row.original.employeeId || '—',
    },
    {
      accessorKey: 'startedOn',
      header: 'Started',
      cell: ({ row }) => String(row.original.startedOn ?? ''),
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: ({ row }) => row.original.state === 'ACTIVE'
        ? <Badge>Active</Badge>
        : <Badge variant="secondary">Inactive</Badge>,
      meta: {
        facet: {
          title: 'State',
          getKey: (staffMember: any) => staffMember.state,
          getLabel: (staffMember: any) =>
            staffMember.state === 'ACTIVE' ? 'Active' : 'Inactive',
        },
      },
    },
  ], []);

  if (!school) {
    return <div className="p-6"><p>Loading…</p></div>;
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">Staff</h2>
          <p className="text-muted-foreground">Manage teachers, aides, admins, and other school staff</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setInviteConfirmOpen(true)} disabled={invitingStaff} className="gap-2">
            <Mail className="h-4 w-4" />
            {invitingStaff ? 'Inviting...' : 'Invite Staff Without Accounts'}
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Staff Member
          </Button>
        </div>
      </div>

      {inviteOutcome && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" role="status">
          <p>{inviteOutcome.sent}</p>
          {inviteOutcome.skipped.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {inviteOutcome.skipped.map((line) => <li key={line}>{line}</li>)}
            </ul>
          )}
        </div>
      )}
      {inviteErrors.length > 0 && (
        <div className="space-y-1 rounded-md border border-destructive/30 px-4 py-3 text-sm text-destructive" role="alert">
          {inviteErrors.map((message) => <p key={message}>{message}</p>)}
        </div>
      )}

      {loading && allStaff.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">Loading staff…</div>
      ) : (
        <DataTable
          columns={columns}
          data={allStaff}
          enableRowSelection
          searchColumn="search"
          searchPlaceholder="Search name, title, employee ID..."
          defaultSorting={[{ id: 'search', desc: false }]}
          stateKey="staff-list"
          onRowClick={(staffMember) => handleRowClick(staffMember.id)}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
        />
      )}

      <AlertDialog open={inviteConfirmOpen} onOpenChange={setInviteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Invite every staff member without an account?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing accounts and pending invitations will be skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleInviteAllStaff}>Send invitations</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateStaffMemberDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        schoolId={school.id}
        defaultKind="TEACHER"
        onComplete={() => {
          setCreateOpen(false);
          refetch();
        }}
      />
    </div>
  );
}
